// src/components/ui/forms/TeamMemberDetailsForm/TeamMemberDetailsFormShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  Save,
  Crown,
  Shield,
  User,
  AlertTriangle,
  Lock
} from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { RadioGroup, RadioGroupItem } from '../../radio-group';
import { Alert, AlertDescription } from '../../alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';

import { cn } from '../../../../utils/cn';

const TeamMemberDetailsFormShadcn = ({
  member,
  currentUser,
  onUpdateMember,
  onUpdateRole,
  canEditMember,
  canChangeRole,
  savingMember = false,
  savingRole = false,
  onChangesSaved,
  onChangesDetected
}) => {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    email: member?.email || '',
    role: member?.role || 'staff'
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});

  // Update form when member changes
  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name || '',
        email: member.email || '',
        role: member.role || 'staff'
      });
      setHasChanges(false);
      setErrors({});
    }
  }, [member]);

  // Track changes
  useEffect(() => {
    if (member) {
      const changed =
        formData.name !== member.name ||
        formData.email !== member.email ||
        formData.role !== member.role;

      setHasChanges(changed);
      if (changed && onChangesDetected) {
        onChangesDetected();
      }
    }
  }, [formData, member, onChangesDetected]);

  // Role options
  const roleOptions = [
    {
      value: 'staff',
      label: 'Staff Member',
      description: 'Basic access to core functionality',
      icon: User,
      color: 'text-blue-400'
    },
    {
      value: 'manager',
      label: 'Manager',
      description: 'Team oversight and business management',
      icon: Shield,
      color: 'text-purple-400'
    },
    {
      value: 'admin',
      label: 'Administrator',
      description: 'Full system access and team management',
      icon: Crown,
      color: 'text-amber-400'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSave = async () => {
    try {
      // Validate form
      const newErrors = {};

      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Handle role change separately
      if (formData.role !== member.role && canChangeRole) {
        await onUpdateRole(member.id, formData.role);
      }

      // Handle other member updates
      if ((formData.name !== member.name || formData.email !== member.email) && canEditMember) {
        await onUpdateMember(member.id, {
          name: formData.name,
          email: formData.email
        });
      }

      setHasChanges(false);
      setErrors({});

      if (onChangesSaved) {
        onChangesSaved();
      }
    } catch (error) {
      console.error('Failed to save member details:', error);
      setErrors({ general: 'Failed to save changes. Please try again.' });
    }
  };

  const isOwnProfile = member?.id === currentUser?.id;

  return (
    <div className="p-6 space-y-6">
      {/* Warning for editing own profile */}
      {isOwnProfile && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Your Profile:</strong> You're viewing your own profile. Some changes may require administrator approval.
          </AlertDescription>
        </Alert>
      )}

      {/* General Error */}
      {errors.general && (
        <Alert variant="destructive">
          <AlertDescription>{errors.general}</AlertDescription>
        </Alert>
      )}

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>
            Update the member's basic profile information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={!canEditMember}
              className={cn(
                errors.name && "border-red-500 focus-visible:ring-red-500"
              )}
              required
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={!canEditMember}
              className={cn(
                errors.email && "border-red-500 focus-visible:ring-red-500"
              )}
              required
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role Management */}
      <Card>
        <CardHeader>
          <CardTitle>Role & Permissions</CardTitle>
          <CardDescription>
            {canChangeRole
              ? "Select the role that defines this member's access level and permissions"
              : "Role management is restricted for this member"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {canChangeRole ? (
            <RadioGroup
              value={formData.role}
              onValueChange={(value) => handleInputChange('role', value)}
              className="space-y-3"
            >
              {roleOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={option.value}
                      className={cn(
                        "flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all w-full peer-data-[state=checked]:bg-primary/10 peer-data-[state=checked]:border-primary/50",
                        formData.role === option.value
                          ? "bg-primary/10 border-primary/50"
                          : "bg-muted/30 border-border hover:border-muted-foreground/50"
                      )}
                    >
                      <RadioGroupItem
                        value={option.value}
                        className="mt-0.5"
                      />
                      <Icon className={cn("w-5 h-5 mt-0.5", option.color)} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{option.label}</span>
                          {formData.role === option.value && (
                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm mt-1">{option.description}</p>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-muted/50 border border-border rounded-lg">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">Role Management Restricted</p>
                <p className="text-muted-foreground text-sm">
                  You don't have permission to change this member's role.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      {(canEditMember || canChangeRole) && hasChanges && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
          </div>

          <Button
            onClick={handleSave}
            disabled={savingMember || savingRole || !hasChanges}
          >
            {(savingMember || savingRole) ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TeamMemberDetailsFormShadcn;